import api from './api';

/**
 * Normalizacja odpowiedzi listy kontrahentow.
 * Backend po zmianie zwraca { success, data: { items, total, page, per_page } },
 * ale starsze wersje zwracaja plaska tablice albo { success, data: [...] }.
 * Ta funkcja sprowadza oba ksztalty do jednego kontraktu.
 */
function normalizeCustomerList(payload, requestedPage, requestedPerPage) {
  const body = (payload && payload.data !== undefined) ? payload.data : payload;

  // Starszy ksztalt - plaska tablica rekordow
  if (Array.isArray(body)) {
    return {
      items: body,
      total: Number(payload?.total ?? body.length) || body.length,
      page: Number(requestedPage) || 1,
      per_page: Number(requestedPerPage) || body.length
    };
  }

  const rawItems = body?.items || body?.customers || body?.klienci || [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const total = body?.total ?? payload?.total ?? items.length;
  const page = body?.page ?? payload?.page ?? requestedPage ?? 1;
  const perPage = body?.per_page ?? payload?.per_page ?? requestedPerPage ?? items.length;

  return {
    items,
    total: Number(total) || 0,
    page: Number(page) || 1,
    per_page: Number(perPage) || items.length
  };
}

export const customerService = {
  /**
   * Pobierz liste kontrahentow (odbiorcy + dostawcy w jednej kartotece).
   *
   * @param {Object} params
   *   search   - fraza wyszukiwania (serwerowa)
   *   rola     - 'odbiorca' | 'dostawca' | '' (wszyscy)
   *   aktywny  - 1 (tylko aktywne) | 'all' (razem z nieaktywnymi i scalonymi)
   *   page     - numer strony (od 1)
   *   per_page - liczba rekordow na stronie
   *   sort     - kolumna sortowania
   *   sort_dir - 'asc' | 'desc'
   * @returns {Promise<{items: Array, total: number, page: number, per_page: number}>}
   */
  async getCustomers(params = {}) {
    try {
      const query = {};
      if (params.search) query.search = params.search;
      if (params.rola) query.rola = params.rola;
      if (params.aktywny !== undefined && params.aktywny !== null && params.aktywny !== '') {
        query.aktywny = params.aktywny;
      }
      if (params.page) query.page = params.page;
      if (params.per_page) query.per_page = params.per_page;
      if (params.sort) query.sort = params.sort;
      if (params.sort_dir) query.sort_dir = params.sort_dir;

      const response = await api.get('/customers', { params: query });
      return normalizeCustomerList(response.data, params.page, params.per_page);
    } catch (error) {
      console.error('Błąd pobierania klientów:', error);
      throw error;
    }
  },

  /**
   * Dodaj nowego kontrahenta
   * @param {Object} customerData - Dane kontrahenta (moze zawierac force: true)
   * @returns {Promise} Utworzony kontrahent
   */
  async createCustomer(customerData) {
    try {
      const response = await api.post('/customers', customerData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Błąd tworzenia klienta:', error);
      throw error;
    }
  },

  /**
   * Aktualizuj kontrahenta
   * UWAGA: NIE wysylamy location_id - kartoteka jest wspolna dla sieci,
   * a kazdy zapis z location_id przestemplowywal karte na sklep edytujacego.
   * @param {number} customerId - ID kontrahenta
   * @param {Object} customerData - Dane kontrahenta
   * @returns {Promise} Zaktualizowany kontrahent
   */
  async updateCustomer(customerId, customerData) {
    try {
      const response = await api.put(`/customers/${customerId}`, customerData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Błąd aktualizacji klienta:', error);
      throw error;
    }
  },

  /**
   * Dezaktywuj kontrahenta (backend robi soft-delete, nie kasuje dokumentow)
   * @param {number} customerId - ID kontrahenta
   * @returns {Promise} Wynik operacji (moze zawierac liczbe powiazanych dokumentow)
   */
  async deleteCustomer(customerId) {
    try {
      const response = await api.delete(`/customers/${customerId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd dezaktywacji klienta:', error);
      throw error;
    }
  },

  /**
   * Wyszukaj kontrahentow
   * @param {string} query - Zapytanie wyszukiwania
   * @param {number} limit - Limit wynikow (default: 10)
   * @param {string} rola - 'odbiorca' | 'dostawca' | '' (wszyscy)
   * @returns {Promise} Wyniki wyszukiwania (surowa odpowiedz backendu)
   */
  async searchCustomers(query, limit = 10, rola = '') {
    try {
      const params = { query, limit };
      if (rola) params.rola = rola;
      const response = await api.get('/customers/search', { params });
      return response.data;
    } catch (error) {
      console.error('Błąd wyszukiwania klientów:', error);
      throw error;
    }
  },

  /**
   * Pobierz szczegoly kontrahenta
   * @param {number} customerId - ID kontrahenta
   * @returns {Promise} Dane kontrahenta
   */
  async getCustomer(customerId) {
    try {
      const response = await api.get(`/customers/${customerId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Błąd pobierania klienta:', error);
      throw error;
    }
  },

  /**
   * Pobierz statystyki kartoteki (liczone po stronie serwera, nie z obcietej listy)
   * @returns {Promise<Object>} Statystyki
   */
  async getStats() {
    try {
      const response = await api.get('/customers/stats');
      return response.data?.data || response.data || {};
    } catch (error) {
      console.error('Błąd pobierania statystyk klientów:', error);
      throw error;
    }
  },

  /**
   * Scal dwie karty kontrahenta.
   * Operacja jest miekka: karta zrodlowa dostaje scalony_z_id + aktywny = 0,
   * a dokumenty sa przepinane na karte docelowa.
   * @param {number} celId - ID karty docelowej (zostaje)
   * @param {number} zrodloId - ID karty zrodlowej (zostaje oznaczona jako scalona)
   * @param {string} powod - Uzasadnienie operacji (trafia do audytu)
   * @returns {Promise} Wynik operacji
   */
  async scalCustomers(celId, zrodloId, powod = '') {
    try {
      const response = await api.post(`/customers/${celId}/scal`, {
        zrodlo_id: zrodloId,
        powod: powod || ''
      });
      return response.data;
    } catch (error) {
      console.error('Błąd scalania kartotek:', error);
      throw error;
    }
  },

  /**
   * Zapisz zgody marketingowe kontrahenta (SMS / e-mail rozdzielone)
   * @param {number} customerId - ID kontrahenta
   * @param {Object} zgody - { zgoda_sms, zgoda_email, zgoda_zrodlo }
   * @returns {Promise} Wynik operacji
   */
  async updateZgody(customerId, zgody = {}) {
    try {
      const response = await api.put(`/customers/${customerId}/zgody`, {
        zgoda_sms: zgody.zgoda_sms ? 1 : 0,
        zgoda_email: zgody.zgoda_email ? 1 : 0,
        zgoda_zrodlo: zgody.zgoda_zrodlo || ''
      });
      return response.data;
    } catch (error) {
      console.error('Błąd zapisu zgód marketingowych:', error);
      throw error;
    }
  },

  /**
   * Pobierz historie kontrahenta (paragony, zamowienia, faktury, FZ, PZ)
   * @param {number} customerId - ID kontrahenta
   * @returns {Promise} Historia
   */
  async getHistory(customerId) {
    try {
      const response = await api.get(`/customers/${customerId}/history`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania historii klienta:', error);
      throw error;
    }
  }
};

// Aliasy dla kompatybilnosci
customerService.addCustomer = customerService.createCustomer;
customerService.getById = customerService.getCustomer;
customerService.getCustomersStats = customerService.getStats;
